import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'react-feather';

import { Button } from '~ui/base/button';
import { Typography } from '~ui/base/typography';
import { cn } from '~lib/utils/style';
import { formatBytes } from '~lib/utils/format-bytes';
import {
  useUploadDataset,
  type FileUpload,
} from '~lib/storage/use-upload-dataset';

interface UploadDialogProps {
  existingNames: string[];
  onClose: () => void;
  onUploaded: () => void;
}

// Pure alphanumeric names — any case, letters and digits only. No
// punctuation, no whitespace, no slashes. Azure blob names are case-sensitive,
// so `Foo` and `foo` are distinct datasets.
const NAME_PATTERN = /^[A-Za-z0-9]+$/;

function UploadDialog({ existingNames, onClose, onUploaded }: UploadDialogProps) {
  const [name, setName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const filePickerRef = useRef<HTMLInputElement>(null);
  const folderPickerRef = useRef<HTMLInputElement>(null);

  const uploader = useUploadDataset();
  const isRunning = uploader.state === 'running';
  const isDone = uploader.state === 'done';

  // Notify the parent once a successful run finishes, so the dataset list
  // refreshes in the background while the user is still reading the summary.
  useEffect(() => {
    if (isDone) onUploaded();
  }, [isDone, onUploaded]);

  const nameMessage = useMemo<{
    text: string;
    severity: 'block' | 'warn';
  } | null>(() => {
    if (!name) return null;
    if (!NAME_PATTERN.test(name)) {
      return {
        text: 'Letters and digits only — no spaces or punctuation.',
        severity: 'block',
      };
    }
    if (existingNames.includes(name)) {
      return {
        text: 'A dataset with this name already exists — files will merge in.',
        severity: 'warn',
      };
    }
    // Azure blob names are case-sensitive, so `Foo` and `foo` would live in
    // separate folders. Warn (don't block) — most of the time this is a typo
    // the user wants to know about, but legitimate intentional cases exist.
    const caseClash = existingNames.find(
      (n) => n.toLowerCase() === name.toLowerCase(),
    );
    if (caseClash) {
      return {
        text: `Casing differs from existing dataset "${caseClash}" — this will create a separate dataset (Azure is case-sensitive).`,
        severity: 'warn',
      };
    }
    return null;
  }, [name, existingNames]);

  const canSubmit =
    (!nameMessage || nameMessage.severity === 'warn') &&
    name.length > 0 &&
    files.length > 0 &&
    !isRunning &&
    !isDone;

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    setFiles(Array.from(incoming));
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 px-4 backdrop-blur-sm"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[1.25rem] bg-card shadow-md">
        <header className="flex items-start justify-between gap-x-6 px-8 pt-8">
          <div>
            <Typography variant="headline-md" as="h2" id="upload-dialog-title">
              Upload a dataset
            </Typography>
            <Typography
              variant="body-md"
              as="p"
              className="mt-2 text-muted-foreground"
            >
              Pick a name, then files or a folder. Uploads land at{' '}
              <span className="font-mono">datasets/&lt;name&gt;/…</span>, with
              any picked folder&apos;s subfolder structure preserved. To enable
              the archive-download button on the row, also include a file named{' '}
              <span className="font-mono">&lt;name&gt;.zip</span> alongside the
              loose files.
            </Typography>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isRunning}
            aria-label="Close"
            className={cn(
              'inline-flex size-8 shrink-0 items-center justify-center rounded-full',
              'text-muted-foreground transition-colors hover:bg-card-highlight hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
              'disabled:pointer-events-none disabled:opacity-60',
            )}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="flex flex-col gap-y-6">
            <div className="flex flex-col gap-y-2">
              <label
                htmlFor="dataset-name"
                className="text-xs font-medium tracking-wide text-muted-foreground"
              >
                DATASET NAME
              </label>
              <input
                id="dataset-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isRunning || isDone}
                placeholder="e.g. ClinicalTrials2024"
                spellCheck={false}
                autoComplete="off"
                className={cn(
                  'rounded-full border border-border bg-input px-4 py-2.5',
                  'font-mono text-sm text-foreground placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-4 focus:ring-offset-card',
                  'disabled:opacity-60',
                )}
              />
              {nameMessage && (
                <Typography
                  variant="caption-md"
                  as="span"
                  className={cn(
                    nameMessage.severity === 'block'
                      ? 'text-destructive'
                      : 'text-muted-foreground-dark',
                  )}
                >
                  {nameMessage.text}
                </Typography>
              )}
            </div>

            <div className="flex flex-col gap-y-3">
              <span className="text-xs font-medium tracking-wide text-muted-foreground">
                FILES
              </span>
              <div className="flex flex-wrap gap-x-3 gap-y-2">
                <Button
                  variant="secondary"
                  size="md"
                  type="button"
                  onClick={() => filePickerRef.current?.click()}
                  disabled={isRunning || isDone}
                >
                  Choose files
                </Button>
                <Button
                  variant="tertiary"
                  size="md"
                  type="button"
                  onClick={() => folderPickerRef.current?.click()}
                  disabled={isRunning || isDone}
                >
                  Choose folder
                </Button>
                <input
                  ref={filePickerRef}
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <input
                  ref={folderPickerRef}
                  type="file"
                  multiple
                  // Non-standard but widely supported folder picker.
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error — webkitdirectory is not in the DOM types.
                  webkitdirectory=""
                  className="sr-only"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>
              {files.length > 0 && (
                <FileList
                  files={files}
                  uploads={uploader.uploads}
                  isRunning={isRunning}
                  onRemove={removeFile}
                />
              )}
            </div>

            {uploader.uploads.length > 0 && (
              <OverallProgress
                totalBytes={uploader.totalBytes}
                bytesUploaded={uploader.bytesUploaded}
                state={uploader.state}
              />
            )}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-x-3 border-t border-border bg-background/40 px-8 py-5">
          {isRunning ? (
            <Button variant="tertiary" size="md" onClick={uploader.cancel}>
              Cancel upload
            </Button>
          ) : (
            <Button variant="tertiary" size="md" onClick={onClose}>
              Close
            </Button>
          )}
          <Button
            size="md"
            onClick={() => uploader.start(name, files)}
            disabled={!canSubmit}
          >
            {isDone ? 'Uploaded' : `Upload ${files.length || ''} file${files.length === 1 ? '' : 's'}`.trim()}
          </Button>
        </footer>
      </div>
    </div>
  );
}

interface FileListProps {
  files: File[];
  uploads: FileUpload[];
  isRunning: boolean;
  onRemove: (index: number) => void;
}

function FileList({ files, uploads, isRunning, onRemove }: FileListProps) {
  return (
    <ul className="divide-y divide-border rounded-[1rem] border border-border">
      {files.map((file, i) => {
        const upload = uploads[i];
        const relativePath =
          (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
          file.name;
        const percent = upload && upload.file.size > 0
          ? Math.round((upload.bytesUploaded / upload.file.size) * 100)
          : null;
        return (
          <li
            key={`${relativePath}-${i}`}
            className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 px-4 py-2.5"
          >
            <div className="flex flex-col gap-y-0.5 overflow-hidden">
              <Typography
                variant="body-md"
                as="span"
                className="truncate font-mono"
              >
                {relativePath}
              </Typography>
              {upload?.error && (
                <Typography
                  variant="caption-md"
                  as="span"
                  className="truncate text-destructive"
                >
                  {upload.error}
                </Typography>
              )}
            </div>
            <Typography
              variant="caption-md"
              as="span"
              className="tabular-nums text-muted-foreground"
            >
              {upload && percent !== null && upload.status === 'uploading'
                ? `${percent}%`
                : formatBytes(file.size)}
            </Typography>
            <StatusOrRemove
              upload={upload}
              canRemove={!isRunning}
              onRemove={() => onRemove(i)}
            />
          </li>
        );
      })}
    </ul>
  );
}

function StatusOrRemove({
  upload,
  canRemove,
  onRemove,
}: {
  upload: FileUpload | undefined;
  canRemove: boolean;
  onRemove: () => void;
}) {
  if (upload && upload.status !== 'queued') {
    return (
      <Typography
        variant="caption-md"
        as="span"
        className={cn(
          'min-w-16 text-right tracking-wide',
          upload.status === 'done' && 'text-success-dark',
          upload.status === 'failed' && 'text-destructive',
          upload.status === 'cancelled' && 'text-muted-foreground-dark',
          upload.status === 'uploading' && 'text-primary',
        )}
      >
        {upload.status === 'done' && 'Done'}
        {upload.status === 'uploading' && 'Uploading'}
        {upload.status === 'failed' && 'Failed'}
        {upload.status === 'cancelled' && 'Cancelled'}
      </Typography>
    );
  }

  if (!canRemove) {
    return (
      <Typography
        variant="caption-md"
        as="span"
        className="min-w-16 text-right tracking-wide text-muted-foreground"
      >
        Queued
      </Typography>
    );
  }

  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label="Remove file"
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-full',
        'text-muted-foreground transition-colors hover:bg-card-highlight hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
      )}
    >
      <X size={14} aria-hidden />
    </button>
  );
}

interface OverallProgressProps {
  totalBytes: number;
  bytesUploaded: number;
  state: ReturnType<typeof useUploadDataset>['state'];
}

function OverallProgress({
  totalBytes,
  bytesUploaded,
  state,
}: OverallProgressProps) {
  const percent = totalBytes > 0 ? (bytesUploaded / totalBytes) * 100 : 0;
  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex items-baseline justify-between">
        <Typography
          variant="caption-md"
          as="span"
          className="text-muted-foreground"
        >
          {formatBytes(bytesUploaded)} of {formatBytes(totalBytes)}
        </Typography>
        <Typography
          variant="caption-md"
          as="span"
          className="tabular-nums text-muted-foreground"
        >
          {Math.round(percent)}%
        </Typography>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-card-highlight">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-300',
            state === 'failed' ? 'bg-destructive' : 'bg-primary',
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export { UploadDialog };
